// src/pages/alerts/AlertsOnboarding.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  BellOff,
  Check,
  ShoppingCart,
  Sprout,
  Truck,
  Store,
  Repeat,
  Utensils,
  Plus,
  Search,
  ShoppingBasket,
  MapPin,
  Bell,
  ChevronDown,
  ChevronRight,
  Pencil,
} from 'lucide-react';
import { useGlobalFontSize } from '../../context/GlobalFontSizeContext';
import { useCommodities } from '../../utils/api-hooks/commodity';

const PRESET_COMMODITIES = [
  'Strawberries',
  'Avocados',
  'Cherries',
  'Blueberries',
  'Grapes',
  'Tomatoes',
  'Apples',
  'Pears',
  'Blackberries',
  'Lettuce',
  'Peppers',
];

const Stepper = ({ step }) => {
  const steps = [1, 2, 3];
  return (
    <div className="flex items-center justify-center">
      {steps.map((s, i) => {
        const active = step === s;
        const done = step > s;
        return (
          <React.Fragment key={s}>
            <div
              className={`flex h-7 w-7 items-center justify-center rounded-full border text-[13px] font-semibold font-['Inter'] transition-colors ${
                active || done
                  ? 'border-bloom-green bg-bloom-green text-white'
                  : 'border-[#D5D8D6] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] text-[#828382]'
              }`}
            >
              {done ? <Check size={14} /> : s}
            </div>
            {i < steps.length - 1 && (
              <div
                className={`h-[2px] w-10 sm:w-16 ${
                  step > s ? 'bg-bloom-green' : 'bg-[#E3E5E4] dark:bg-[#5d625f]'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const NoAlertsBadge = () => {
  const { t } = useTranslation();
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full bg-[#FDEBD3] dark:bg-[#3a2f1d] px-3 py-1.5">
      <BellOff size={14} className="text-[#C77D2E]" />
      <span className="font-['Inter'] text-[13px] font-medium text-[#C77D2E]">
        {t('alerts.onboarding.badge')}
      </span>
    </div>
  );
};

const Chip = ({
  selected,
  onClick,
  children,
  dashed = false,
  icon: Icon,
  onEdit,
  editLabel,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 font-['Inter'] text-[14px] font-medium transition-colors ${
      selected
        ? 'border-bloom-green bg-[#E9FBF0] dark:bg-[#173322] text-[#12823F] dark:text-[#7EE6A4]'
        : dashed
          ? 'border-dashed border-[#C3C4C3] dark:border-[#5d625f] text-[#5D625F] dark:text-[#C3C4C3] hover:border-bloom-green'
          : 'border-[#D5D8D6] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] text-[#282A29] dark:text-white hover:border-bloom-green'
    }`}
  >
    {selected ? (
      <Check size={16} className="text-bloom-green shrink-0" />
    ) : Icon ? (
      <Icon size={16} className="text-[#828382] shrink-0" />
    ) : null}
    <span className="min-w-0">{children}</span>
    {onEdit ? (
      <span
        role="button"
        tabIndex={0}
        aria-label={editLabel}
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onEdit();
          }
        }}
        className="inline-flex shrink-0 rounded-full p-0.5 text-[#12823F] dark:text-[#7EE6A4] hover:bg-bloom-green/10 transition-colors"
      >
        <Pencil size={14} />
      </span>
    ) : null}
  </button>
);

const PrimaryButton = ({ children, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center gap-2 rounded-[8px] bg-bloom-green px-5 py-2.5 font-['Inter'] font-medium text-white transition-colors ${
      disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-bloom-green-hover'
    }`}
  >
    {children}
  </button>
);

const SecondaryButton = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-2 rounded-[8px] border border-bloom-green px-5 py-2.5 font-['Inter'] font-medium text-[#12823F] hover:bg-[#E9FBF0] dark:hover:bg-[#12823F]/10 transition-colors"
  >
    {children}
  </button>
);

const MoreCommodityDropdown = ({ exclude = [], onAdd, label }) => {
  const { t } = useTranslation();
  const { commodities, isLoading } = useCommodities();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pending, setPending] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({ Fruits: true });
  const ref = useRef(null);
  const searchInputRef = useRef(null);

  const excludeLower = useMemo(
    () => new Set(exclude.map((c) => String(c).toLowerCase())),
    [exclude]
  );

  const filteredCommodities = useMemo(() => {
    const sorted = {};
    const query = searchQuery.trim().toLowerCase();
    const categories = Object.keys(commodities || {}).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );

    for (const category of categories) {
      const items = [...(commodities[category] || [])]
        .filter((c) => !excludeLower.has(String(c).toLowerCase()))
        .filter((c) => !query || String(c).toLowerCase().includes(query))
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      if (items.length > 0) sorted[category] = items;
    }
    return sorted;
  }, [commodities, excludeLower, searchQuery]);

  const hasResults = Object.keys(filteredCommodities).length > 0;

  useEffect(() => {
    if (searchQuery) {
      const allExpanded = {};
      Object.keys(filteredCommodities).forEach((cat) => {
        allExpanded[cat] = true;
      });
      setExpandedCategories(allExpanded);
    }
  }, [searchQuery, filteredCommodities]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearchQuery('');
        setPending([]);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  const togglePending = (commodity) => {
    setPending((prev) =>
      prev.includes(commodity)
        ? prev.filter((c) => c !== commodity)
        : [...prev, commodity]
    );
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleAdd = () => {
    if (pending.length === 0) return;
    onAdd?.(pending);
    setPending([]);
    setSearchQuery('');
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen((v) => !v);
    if (open) {
      setSearchQuery('');
      setPending([]);
    }
  };

  if (!isLoading && !open) {
    const anyAvailable = Object.values(commodities || {}).some((items) =>
      (items || []).some((c) => !excludeLower.has(String(c).toLowerCase()))
    );
    if (!anyAvailable) return null;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={handleOpen}
        disabled={isLoading}
        className="inline-flex items-center gap-2 rounded-full border border-dashed border-[#C3C4C3] dark:border-[#5d625f] px-4 py-2.5 font-['Inter'] text-[14px] font-medium text-[#5D625F] dark:text-[#C3C4C3] hover:border-bloom-green transition-colors disabled:opacity-50"
      >
        <Search size={16} className="text-[#828382]" />
        {label}
        <ChevronDown
          size={14}
          className={`text-[#828382] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-1/2 z-30 mt-2 flex w-[min(300px,calc(100vw-32px))] -translate-x-1/2 flex-col overflow-hidden rounded-[12px] border border-[#E3E5E4] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] shadow-lg">
          {/* Search */}
          <div className="border-b border-[#E3E5E4] dark:border-[#5d625f] px-3 py-2.5">
            <div className="relative">
              <Search
                size={16}
                className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 text-[#B0B2B1] dark:text-[#828382]"
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('alerts.onboarding.searchCommodities', 'Search...')}
                className="w-full bg-transparent py-1 pl-7 pr-2 font-['Inter'] text-[14px] text-[#282A29] dark:text-white placeholder:text-[#B0B2B1] dark:placeholder:text-[#828382] outline-none"
              />
            </div>
          </div>

          {/* Nested Fruits / Vegetables list */}
          <div className="max-h-[260px] overflow-y-auto">
            {isLoading ? (
              <p className="px-4 py-6 text-center font-['Inter'] text-[14px] text-[#828382]">
                {t('common.loading', 'Loading...')}
              </p>
            ) : !hasResults ? (
              <p className="px-4 py-6 text-center font-['Inter'] text-[14px] text-[#828382]">
                {t('alerts.onboarding.noCommoditiesFound', 'No commodities found')}
              </p>
            ) : (
              Object.entries(filteredCommodities).map(([category, items]) => (
                <div
                  key={category}
                  className="border-b border-[#E3E5E4] dark:border-[#5d625f] last:border-b-0"
                >
                  <button
                    type="button"
                    onClick={() => toggleCategory(category)}
                    className="flex w-full items-center justify-between bg-[#F7F8F7] dark:bg-[#232121] px-3 py-2 transition-colors hover:bg-[#F0F5F2] dark:hover:bg-[#282a29]"
                  >
                    <span className="font-['Inter'] text-[13px] font-medium text-[#5D625F] dark:text-[#C3C4C3]">
                      {category}
                      <span className="ml-1.5 text-[#B0B2B1] dark:text-[#828382]">
                        ({items.length})
                      </span>
                    </span>
                    <ChevronRight
                      size={14}
                      className={`text-[#828382] transition-transform ${
                        expandedCategories[category] ? 'rotate-90' : ''
                      }`}
                    />
                  </button>

                  {expandedCategories[category] && (
                    <div className="py-1">
                      {items.map((commodity) => {
                        const checked = pending.includes(commodity);
                        return (
                          <button
                            key={commodity}
                            type="button"
                            onClick={() => togglePending(commodity)}
                            className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-[#F0F5F2] dark:hover:bg-[#282a29]"
                          >
                            <span
                              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] border-2 transition-colors ${
                                checked
                                  ? 'border-bloom-green bg-bloom-green'
                                  : 'border-[#C3C4C3] dark:border-[#5d625f] bg-transparent'
                              }`}
                            >
                              {checked && <Check size={12} className="text-white" strokeWidth={3} />}
                            </span>
                            <span className="font-['Inter'] text-[14px] text-[#282A29] dark:text-[#f0f5f2]">
                              {commodity}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 border-t border-[#E3E5E4] dark:border-[#5d625f] px-3 py-2.5">
            <span className="font-['Inter'] text-[13px] font-medium text-bloom-green">
              {t('alerts.onboarding.moreAdded', { count: pending.length })}
            </span>
            <button
              type="button"
              onClick={handleAdd}
              disabled={pending.length === 0}
              className={`rounded-full bg-bloom-green px-4 py-1.5 font-['Inter'] text-[13px] font-medium text-white transition-colors ${
                pending.length === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-bloom-green-hover'
              }`}
            >
              {t('alerts.onboarding.add', 'Add')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AlertsOnboarding = ({ userName, onComplete }) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();

  const [step, setStep] = useState(1);
  const [selectedCommodities, setSelectedCommodities] = useState([]);
  const [addedCommodities, setAddedCommodities] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [otherInputOpen, setOtherInputOpen] = useState(false);
  const [otherDraft, setOtherDraft] = useState('');
  const [editingCustomId, setEditingCustomId] = useState(null);
  const otherInputRef = useRef(null);

  const presetRoleOptions = [
    { id: 'buyer', label: t('alerts.onboarding.roles.buyer'), icon: ShoppingCart },
    { id: 'grower', label: t('alerts.onboarding.roles.grower'), icon: Sprout },
    { id: 'distributor', label: t('alerts.onboarding.roles.distributor'), icon: Truck },
    { id: 'retailer', label: t('alerts.onboarding.roles.retailer'), icon: Store },
    { id: 'broker', label: t('alerts.onboarding.roles.broker'), icon: Repeat },
    { id: 'foodservice', label: t('alerts.onboarding.roles.foodservice'), icon: Utensils },
  ];

  const moreExclude = useMemo(
    () => [...PRESET_COMMODITIES, ...addedCommodities],
    [addedCommodities]
  );

  const visibleCommodities = useMemo(
    () => [...PRESET_COMMODITIES, ...addedCommodities],
    [addedCommodities]
  );

  const toggleCommodity = (commodity) => {
    setSelectedCommodities((prev) =>
      prev.includes(commodity)
        ? prev.filter((c) => c !== commodity)
        : [...prev, commodity]
    );
  };

  const handleMoreCommoditiesAdd = (commoditiesToAdd) => {
    setAddedCommodities((prev) => {
      const next = [...prev];
      commoditiesToAdd.forEach((commodity) => {
        if (!next.includes(commodity)) next.push(commodity);
      });
      return next;
    });
    setSelectedCommodities((prev) => {
      const next = [...prev];
      commoditiesToAdd.forEach((commodity) => {
        if (!next.includes(commodity)) next.push(commodity);
      });
      return next;
    });
  };

  const toggleRole = (roleId) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const openOtherInput = (customId = null) => {
    if (customId) {
      const existing = customRoles.find((r) => r.id === customId);
      setEditingCustomId(customId);
      setOtherDraft(existing?.label || '');
    } else {
      setEditingCustomId(null);
      setOtherDraft('');
    }
    setOtherInputOpen(true);
  };

  const closeOtherInput = () => {
    setOtherInputOpen(false);
    setOtherDraft('');
    setEditingCustomId(null);
  };

  const saveOtherRole = () => {
    const label = otherDraft.trim();
    if (!label) return;

    // Backend not ready yet — persist in local onboarding state for now.
    if (editingCustomId) {
      setCustomRoles((prev) =>
        prev.map((role) => (role.id === editingCustomId ? { ...role, label } : role))
      );
    } else {
      const id = `custom:${Date.now()}`;
      setCustomRoles((prev) => [...prev, { id, label }]);
      setSelectedRoles((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
    closeOtherInput();
  };

  useEffect(() => {
    if (otherInputOpen && otherInputRef.current) {
      otherInputRef.current.focus();
      otherInputRef.current.select?.();
    }
  }, [otherInputOpen, editingCustomId]);

  const handleFinish = () => {
    // Dismiss onboarding and open the alerts hub — do not create alerts.
    // selectedRoles + customRoles are ready to send once the backend endpoint exists.
    onComplete?.({
      commodities: selectedCommodities,
      roles: selectedRoles,
      customRoles,
    });
  };

  const displayName = userName || t('alerts.onboarding.defaultName');

  return (
    <div className="flex w-full min-h-full flex-col bg-white bg-dark-app">
      <div className="mx-auto flex w-full max-w-[820px] flex-1 flex-col items-center px-4 py-8 sm:py-10">
        <div className="flex w-full items-center justify-center">
          <Stepper step={step} />
        </div>

        {/* Badge */}
        {step < 3 && (
          <div className="mt-5">
            <NoAlertsBadge />
          </div>
        )}

        {/* Step content is vertically centered in the remaining space (BiQ empty-state pattern) */}
        <div className="flex w-full min-h-0 flex-1 flex-col items-center justify-center py-10">
          {/* Step 1: Commodities */}
          {step === 1 && (
            <div className="flex w-full flex-col items-center">
              <h1
                className="text-center font-['Inter'] font-bold text-[#282A29] dark:text-white"
                style={{ fontSize: fontSize + 12 }}
              >
                {t('alerts.onboarding.step1TitleStart')}
                <span className="text-bloom-green">{displayName}</span>
                {t('alerts.onboarding.step1TitleEnd')}
              </h1>
              <p
                className="mt-3 max-w-[560px] text-center font-['Inter'] text-[#5D625F] dark:text-[#C3C4C3]"
                style={{ fontSize }}
              >
                {t('alerts.onboarding.step1Subtitle')}
              </p>

              <div className="mt-[56px] flex max-w-[640px] flex-wrap items-center justify-center gap-3">
                {visibleCommodities.map((commodity) => (
                  <Chip
                    key={commodity}
                    selected={selectedCommodities.includes(commodity)}
                    onClick={() => toggleCommodity(commodity)}
                  >
                    {commodity}
                  </Chip>
                ))}
                <MoreCommodityDropdown
                  exclude={moreExclude}
                  onAdd={handleMoreCommoditiesAdd}
                  label={t('alerts.onboarding.more')}
                />
              </div>

              <p className="mt-6 font-['Inter'] text-[14px] font-medium text-bloom-green">
                {t('alerts.onboarding.commoditiesSelected', {
                  count: selectedCommodities.length,
                })}
              </p>

              <div className="mt-[56px]">
                <PrimaryButton
                  onClick={() => setStep(2)}
                  disabled={selectedCommodities.length === 0}
                >
                  {t('alerts.onboarding.next')}
                  <ArrowRight size={16} />
                </PrimaryButton>
              </div>
            </div>
          )}

          {/* Step 2: Line of work */}
          {step === 2 && (
            <div className="flex w-full flex-col items-center">
              <h1
                className="text-center font-['Inter'] font-bold text-[#282A29] dark:text-white"
                style={{ fontSize: fontSize + 12 }}
              >
                {t('alerts.onboarding.step2Title')}
              </h1>
              <p
                className="mt-3 max-w-[560px] text-center font-['Inter'] text-[#5D625F] dark:text-[#C3C4C3]"
                style={{ fontSize }}
              >
                {t('alerts.onboarding.step2Subtitle')}
              </p>
              <p
                className="mt-1 text-center font-['Inter'] font-semibold text-[#282A29] dark:text-white"
                style={{ fontSize }}
              >
                {t('alerts.onboarding.step2SkipNote')}
              </p>

              <div className="mt-[56px] flex max-w-[640px] flex-col items-center gap-4">
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {presetRoleOptions.map((option) => (
                    <Chip
                      key={option.id}
                      icon={option.icon}
                      selected={selectedRoles.includes(option.id)}
                      onClick={() => toggleRole(option.id)}
                    >
                      {option.label}
                    </Chip>
                  ))}

                  {customRoles.map((role) => (
                    <Chip
                      key={role.id}
                      selected={selectedRoles.includes(role.id)}
                      onClick={() => toggleRole(role.id)}
                      onEdit={() => openOtherInput(role.id)}
                      editLabel={t('alerts.onboarding.editRole', 'Edit role')}
                    >
                      {role.label}
                    </Chip>
                  ))}

                  <Chip
                    icon={Plus}
                    dashed
                    selected={false}
                    onClick={() => openOtherInput()}
                  >
                    {t('alerts.onboarding.roles.other')}
                  </Chip>
                </div>

                {otherInputOpen && (
                  <div className="flex w-full max-w-[420px] flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      ref={otherInputRef}
                      type="text"
                      value={otherDraft}
                      onChange={(e) => setOtherDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          saveOtherRole();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          closeOtherInput();
                        }
                      }}
                      placeholder={t(
                        'alerts.onboarding.otherRolePlaceholder',
                        'Describe your role...'
                      )}
                      className="min-w-0 flex-1 rounded-[8px] border border-[#D5D8D6] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] px-3 py-2.5 font-['Inter'] text-[14px] text-[#282A29] dark:text-white placeholder:text-[#828382] outline-none focus:border-bloom-green"
                    />
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={closeOtherInput}
                        className="rounded-[8px] border border-[#D5D8D6] dark:border-[#5d625f] px-4 py-2.5 font-['Inter'] text-[14px] font-medium text-[#5D625F] dark:text-[#C3C4C3] hover:bg-[#F0F5F2] dark:hover:bg-[#282a29] transition-colors"
                      >
                        {t('common.cancel', 'Cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={saveOtherRole}
                        disabled={!otherDraft.trim()}
                        className={`rounded-[8px] bg-bloom-green px-4 py-2.5 font-['Inter'] text-[14px] font-medium text-white transition-colors ${
                          otherDraft.trim()
                            ? 'hover:bg-bloom-green-hover'
                            : 'opacity-50 cursor-not-allowed'
                        }`}
                      >
                        {t('alerts.onboarding.saveRole', 'Save')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-[56px] flex items-center gap-3">
                <SecondaryButton onClick={() => setStep(1)}>
                  {t('alerts.onboarding.back')}
                </SecondaryButton>
                <PrimaryButton onClick={() => setStep(3)}>
                  {t('alerts.onboarding.next')}
                  <ArrowRight size={16} />
                </PrimaryButton>
              </div>
            </div>
          )}

          {/* Step 3: Summary */}
          {step === 3 && (
            <div className="flex w-full flex-col items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E9FBF0]">
                <Check size={26} className="text-bloom-green" />
              </div>
              <h1
                className="mt-4 text-center font-['Inter'] font-bold text-[#282A29] dark:text-white"
                style={{ fontSize: fontSize + 12 }}
              >
                {t('alerts.onboarding.step3Title')}
              </h1>
              <p
                className="mt-3 max-w-[560px] text-center font-['Inter'] text-[#5D625F] dark:text-[#C3C4C3]"
                style={{ fontSize }}
              >
                {t('alerts.onboarding.step3Subtitle')}
              </p>

              <div className="mt-[56px] w-full max-w-[520px] overflow-hidden rounded-[12px] border border-[#E3E5E4] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d]">
                <SummaryRow
                  icon={ShoppingBasket}
                  title={t('alerts.onboarding.summaryCommodities', {
                    count: selectedCommodities.length,
                  })}
                  detail={selectedCommodities.join(' · ')}
                />
                <SummaryRow
                  icon={MapPin}
                  title={t('alerts.onboarding.summaryOrigins')}
                  detail={t('alerts.onboarding.summaryOriginsDetail')}
                  divider
                />
                <SummaryRow
                  icon={Bell}
                  title={t('alerts.onboarding.summaryTypes')}
                  detail={t('alerts.onboarding.summaryTypesDetail')}
                  divider
                />
              </div>

              <div className="mt-[56px] flex items-center gap-3">
                <SecondaryButton onClick={() => setStep(2)}>
                  {t('alerts.onboarding.back')}
                </SecondaryButton>
                <PrimaryButton onClick={handleFinish}>
                  {t('alerts.onboarding.goToAlerts')}
                </PrimaryButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryRow = ({ icon: Icon, title, detail, divider }) => {
  const { fontSize } = useGlobalFontSize();
  return (
    <div
      className={`flex items-center gap-3 px-5 py-4 ${
        divider ? 'border-t border-[#E3E5E4] dark:border-[#5d625f]' : ''
      }`}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E9FBF0]">
        <Icon size={18} className="text-bloom-green" />
      </span>
      <div className="min-w-0">
        <p
          className="font-['Inter'] font-semibold text-[#282A29] dark:text-white"
          style={{ fontSize }}
        >
          {title}
        </p>
        {detail ? (
          <p
            className="mt-0.5 font-['Inter'] text-[#828382] dark:text-[#C3C4C3]"
            style={{ fontSize: fontSize - 2 }}
          >
            {detail}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default AlertsOnboarding;
